import { OrderMapper } from "@/adapters/Order.mapper";
import { RouteMapper } from "@/adapters/Route.mapper";
import { RoutePointMapper } from "@/adapters/RoutePoint.mapper";
import { getRootStore } from "@/stores/RootStore";
import { RouteStatus, } from "@/types/route/route.common";
import { supabase } from "@/utils/supabase";
/**
 * Concrete implementation of RouteService
 * Handles all database operations for routes
 */
export class RouteService {
    constructor() { }
    /**
     * Load routes with filters and pagination
     * RQ201: Sort by routeDate descending (newest first)
     * RQ202: Display 15 routes per page
     */
    async list(filters = {}, page = 1, pageSize = 15) {
        try {
            const from = (page - 1) * pageSize;
            const to = from + pageSize - 1;
            // Query the routesSearchView with all necessary fields
            let query = supabase()
                .from("routesSearchView")
                .select(`*`, { count: "exact" })
                .eq("isDeleted", false)
                .order("plannedStartTime", { ascending: false })
                .range(from, to);
            // Status filter - RQ203
            if (filters.routeStatus?.length) {
                query = query.in("routeStatus", filters.routeStatus);
            }
            // Date range filter
            if (filters.routeDate?.dateFrom) {
                query = query.gte("plannedStartTime", filters.routeDate.dateFrom);
            }
            if (filters.routeDate?.dateTo) {
                query = query.lte("plannedStartTime", `${filters.routeDate.dateTo}T23:59:59.999Z`);
            }
            // Search filter - RQ205 (поиск по имени техника)
            // routeId исключён из ilike — это UUID, PostgreSQL не поддерживает ~~* для UUID
            if (filters.search?.trim()) {
                const q = filters.search.trim();
                query = query.ilike("technicianFullName", `%${q}%`);
            }
            const { data, error, count } = await query;
            if (error) {
                throw new Error(`Failed to load routes: ${error.message}`);
            }
            return {
                data: data,
                count: count || 0,
            };
        }
        catch (error) {
            console.error("Error in RouteService.getRoutes:", error);
            throw error;
        }
    }
    /**
     * Load routes with route_points in a single query (Dashboard optimization)
     * Eliminates N+1 problem by fetching all data at once
     */
    async listWithPoints(filters = {}, page = 1, pageSize = 50) {
        try {
            const from = (page - 1) * pageSize;
            const to = from + pageSize - 1;
            // Build query for routes with nested route_points
            let query = supabase()
                .from("routes")
                .select(`
          id,
          idTechnician,
          status,
          plannedStartTime,
          expectedStartTime,
          isDeleted,
          technician:employees!idTechnician(id, firstName, lastName, middleName),
          serviceVehicle:ServiceVehicles!idServiceVehicle(id, brand, model),
          chargingStation:ChargingStations!idChargingStation(id, serialNumber),
          routePoints:route_points!idRoute(
            id,
            orderIndex,
            idRoute,
            idOrder,
            idBase,
            plannedArrivalTime,
            actualArrivalTime,
            plannedDepartureTime,
            actualDepartureTime,
            expectedArrivalTime,
            expectedDepartureTime,
            addressShort,
            isDeleted,
            order:orders!idOrder(orderNumber, addressShort, status)
          )
        `, { count: "exact" })
                .eq("isDeleted", false)
                .order("plannedStartTime", { ascending: false })
                .range(from, to);
            // Status filter
            if (filters.routeStatus?.length) {
                query = query.in("status", filters.routeStatus);
            }
            // Date range filter
            if (filters.routeDate?.dateFrom) {
                query = query.gte("plannedStartTime", filters.routeDate.dateFrom);
            }
            if (filters.routeDate?.dateTo) {
                query = query.lte("plannedStartTime", `${filters.routeDate.dateTo}T23:59:59.999Z`);
            }
            const { data, error, count } = await query;
            if (error) {
                throw new Error(`Failed to load routes with points: ${error.message}`);
            }
            // Transform to RouteSearchViewWithPoints
            const result = (data || []).map((route) => {
                // Filter out deleted route points
                const activePoints = (route.routePoints || []).filter((rp) => !rp.isDeleted);
                // Sort by orderIndex
                activePoints.sort((a, b) => a.orderIndex - b.orderIndex);
                // Calculate aggregated fields
                const ordersCount = activePoints.filter((rp) => rp.idOrder).length;
                const firstPoint = activePoints[0];
                const lastPoint = activePoints[activePoints.length - 1];
                // Handle Supabase relation format (object or array)
                const technician = Array.isArray(route.technician)
                    ? route.technician[0]
                    : route.technician;
                const serviceVehicle = Array.isArray(route.serviceVehicle)
                    ? route.serviceVehicle[0]
                    : route.serviceVehicle;
                const chargingStation = Array.isArray(route.chargingStation)
                    ? route.chargingStation[0]
                    : route.chargingStation;
                return {
                    routeId: route.id,
                    routeStatus: route.status,
                    technicianFullName: technician
                        ? [technician.lastName, technician.firstName, technician.middleName]
                            .filter(Boolean)
                            .join(" ")
                        : null,
                    technicianId: route.idTechnician || "",
                    ordersCount,
                    plannedStartTime: route.plannedStartTime,
                    expectedStartTime: route.expectedStartTime,
                    actualStartTime: firstPoint?.actualDepartureTime || null,
                    plannedEndTime: lastPoint?.plannedArrivalTime || null,
                    expectedEndTime: lastPoint?.expectedArrivalTime || null,
                    actualEndTime: lastPoint?.actualArrivalTime || null,
                    serviceVehicleName: serviceVehicle
                        ? [serviceVehicle.brand, serviceVehicle.model].filter(Boolean).join(" ")
                        : null,
                    chargingStationName: chargingStation?.serialNumber || null,
                    routePoints: activePoints,
                };
            });
            return {
                data: result,
                count: count || 0,
            };
        }
        catch (error) {
            console.error("Error in RouteService.listWithPoints:", error);
            throw error;
        }
    }
    /**
     * Load a specific route with all related data
     */
    async get(id) {
        const { data, error } = await supabase()
            .from("routes")
            .select(`
        *,
        technician:employees!idTechnician(*),
        createdBy:employees!idCreatedBy(firstName, lastName),
        updatedBy:employees!idUpdatedBy(firstName, lastName),
        routePoints:route_points!idRoute(*),
        routePointsWithOrders:route_points!idRoute(*, order:orders!idOrder(*))
      `)
            .eq("id", id)
            .eq("isDeleted", false)
            .single();
        if (error) {
            throw new Error(`Failed to load route: ${error.message}`);
        }
        if (!data) {
            throw new Error("Route not found");
        }
        return RouteMapper.toDomain(data);
    }
    /**
     * Load route with route points for Dashboard
     * Returns raw data without Zod transformation to preserve routePoints
     */
    async getWithPoints(id) {
        const { data, error } = await supabase()
            .from("routes")
            .select(`
        id,
        idTechnician,
        routePoints:route_points!idRoute(
          id,
          orderIndex,
          idRoute,
          idOrder,
          idBase,
          plannedArrivalTime,
          actualArrivalTime,
          plannedDepartureTime,
          actualDepartureTime,
          expectedArrivalTime,
          expectedDepartureTime,
          addressShort,
          order:orders!idOrder(orderNumber, addressShort, status)
        )
      `)
            .eq("id", id)
            .eq("isDeleted", false)
            .single();
        if (error) {
            throw new Error(`Failed to load route with points: ${error.message}`);
        }
        if (!data) {
            throw new Error("Route not found");
        }
        return {
            id: data.id,
            idTechnician: data.idTechnician,
            routePoints: (data.routePoints || []),
        };
    }
    /**
     * Cancel a route (dispatcher action):
     * 1. Set route status = CANCELLED, reason_cancellation
     * 2. Soft-delete all route_points (isDeleted = true)
     * Orders are NOT cancelled — they remain available for reassignment
     */
    async cancel(id, reason) {
        try {
            const now = new Date().toISOString();
            const userId = getRootStore().authStore.employee?.id;
            // 1. Update route status → CANCELLED
            const { data, error } = await supabase()
                .from("routes")
                .update({
                status: RouteStatus.CANCELLED,
                reason_cancellation: reason ?? null,
                updatedAt: now,
                idUpdatedBy: userId,
            })
                .eq("id", id)
                .eq("isDeleted", false)
                .select("*")
                .single();
            if (error) {
                throw new Error(`Failed to cancel route: ${error.message}`);
            }
            if (!data) {
                throw new Error("Route not found");
            }
            // 2. Soft-delete all route_points for this route
            const { error: pointsError } = await supabase()
                .from("route_points")
                .update({
                isDeleted: true,
                updatedAt: now,
                idUpdatedBy: userId,
            })
                .eq("idRoute", id)
                .eq("isDeleted", false);
            if (pointsError) {
                throw new Error(`Failed to soft-delete route points: ${pointsError.message}`);
            }
            return RouteMapper.toDomain(data);
        }
        catch (error) {
            console.error("Error in RouteService.cancel:", error);
            throw error;
        }
    }
    /**
     * Create a new route
     * RQ208: Auto-set base coordinates as start and end points
     * RQ210: Auto-set status = PLANNED, createdBy, createdAt
     */
    async create(routeData) {
        const newRoute = {
            idTechnician: routeData.idTechnician,
            idServiceVehicle: routeData.idServiceVehicle,
            idChargingStation: routeData.idChargingStation,
            status: "CREATED", // RQ210
            plannedStartTime: routeData.plannedStartTime,
            expectedArrivalTime: routeData.expectedArrivalTime,
            idCreatedBy: getRootStore().authStore.employee?.id,
        };
        const { data, error } = await supabase()
            .from("routes")
            .insert(newRoute)
            .select()
            .single();
        if (error) {
            throw new Error(`Failed to create route: ${error.message}`);
        }
        return RouteMapper.toDomain(data);
    }
    /**
     * Atomically create a route with its route_points via PostgreSQL RPC.
     * Uses the create_route_with_points function (created in TECH-001) which
     * wraps both INSERTs in a single transaction — either both succeed or neither.
     */
    async createWithPoints(routeData, routePoints) {
        const routePayload = {
            idTechnician: routeData.idTechnician,
            idServiceVehicle: routeData.idServiceVehicle,
            idChargingStation: routeData.idChargingStation,
            status: "CREATED",
            plannedStartTime: routeData.plannedStartTime,
            expectedArrivalTime: routeData.expectedArrivalTime,
            idCreatedBy: getRootStore().authStore.employee?.id,
        };
        const pointsPayload = routePoints.map((rp, index) => RoutePointMapper.toCreateDto({
            ...rp,
            orderIndex: index,
            isDeleted: false,
        }));
        const { data, error } = await supabase().rpc("create_route_with_points", {
            p_route: routePayload,
            p_points: pointsPayload,
        });
        if (error) {
            throw new Error(`Failed to create route with points: ${error.message}`);
        }
        return RouteMapper.toDomain(data);
    }
    /**
     * Update an existing route and synchronize route points
     */
    async update(id, routeData) {
        try {
            const userId = getRootStore().authStore.employee?.id;
            // Send only real DB columns (exclude PostgREST relationship aliases)
            const { data, error } = await supabase()
                .from("routes")
                .update({
                idTechnician: routeData.idTechnician,
                idServiceVehicle: routeData.idServiceVehicle ?? null,
                idChargingStation: routeData.idChargingStation ?? null,
                plannedStartTime: routeData.plannedStartTime,
                expectedArrivalTime: routeData.expectedArrivalTime ?? null,
                status: routeData.status,
                updatedAt: new Date().toISOString(),
                idUpdatedBy: userId,
            })
                .eq("id", id)
                .eq("isDeleted", false)
                .select(`*`)
                .single();
            if (error) {
                throw new Error(`Failed to update route: ${error.message}`);
            }
            if (!data) {
                throw new Error("Route not found");
            }
            // Convert DTO to Domain object using RouteMapper
            return RouteMapper.toDomain(data);
        }
        catch (error) {
            console.error("Error in RouteService.updateRoute:", error);
            throw error;
        }
    }
    /**
     * Hard delete a CREATED route (physical removal from DB).
     * Route points must be deleted before calling this method.
     */
    async delete(id) {
        try {
            const { error } = await supabase()
                .from("routes")
                .delete()
                .eq("id", id);
            if (error) {
                throw new Error(`Failed to delete route: ${error.message}`);
            }
        }
        catch (error) {
            console.error("Error in RouteService.deleteRoute:", error);
            throw error;
        }
    }
    /**
     * Get available orders for route assignment
     * RQ209: Only orders with NEW and CONFIRMED status, not assigned to other routes
     */
    async getAvailableOrders() {
        const { data, error } = await supabase().rpc("orders_without_route_points", {
            include_deleted: false,
        });
        if (error) {
            console.error("Error loading available orders:", error);
            return [];
        }
        // Transform DTO data to Domain objects using OrderMapper
        return data.map((orderDto) => OrderMapper.toDomain(orderDto));
    }
}
export const routeService = new RouteService();
